import { Component, computed, inject, input, output, signal, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { FunctionApiService, GoalInterface, PromptModuleApiService, PromptModuleKey } from '@vendasta/ai-assistants';
import { BadgeComponent } from '@vendasta/galaxy/badge';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AiCapabilityForm, AiCapabilityFormArray, AiToolForm } from '../../../core/forms';
import { AiSituationConfigForm } from '../../../core/forms/ai-situation-config.form';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { AiToolFormRegistryService } from '../../../core/services/ai-tool-form-registry.service';
import { AiAssistantFormV2Service } from '../../ai-assistant-configuration/ai-assistant-form-v2/ai-assistant-form-v2.service';
import { EnableLeadCaptureDialogComponent } from '../../ai-assistant-configuration/enable-lead-capture-dialog/enable-lead-capture-dialog.component';
import { AiCapabilityConfigurationComponent } from '../ai-capability-configuration/ai-capability-configuration.component';
import {
  AiCapabilityCustomForms,
  AiCapabilityFormLoaderComponent,
} from '../ai-capability-form-loader/ai-capability-form-loader.component';
import { AiCapabilitySelectComponent } from '../ai-capability-select-dialog/ai-capability-select-dialog.component';

@Component({
  selector: 'ai-capabilities-list',
  imports: [
    MatMenuModule,
    MatBadgeModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatIconButton,
    MatDialogModule,
    TranslateModule,
    BadgeComponent,
    AiCapabilityFormLoaderComponent,
    AiCapabilityConfigurationComponent,
  ],
  templateUrl: './ai-capabilities-list.component.html',
  styleUrl: './ai-capabilities-list.component.scss',
  host: {
    class: 'ai-capabilities-list',
  },
})
export class AiCapabilitiesListComponent {
  readonly capabilities = input.required<AiCapabilityFormArray>();
  readonly situationConfig = input<AiSituationConfigForm>();
  readonly capabilityFormDisplay = output<AiCapabilityForm | undefined>();
  readonly rollback = output<AiCapabilityForm>();
  readonly toolFormDisplay = output<{ parentCapabilityForm?: AiCapabilityForm; form: AiToolForm } | undefined>();

  readonly dialog = inject(MatDialog);
  private readonly assistantService = inject(AiAssistantService);
  private readonly aiAssistantServiceV2 = inject(AiAssistantFormV2Service);
  private readonly promptModuleApiService = inject(PromptModuleApiService);
  private readonly functionApiService = inject(FunctionApiService);
  private readonly toolFormRegistry = inject(AiToolFormRegistryService);
  private readonly snackbar = inject(SnackbarService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly confirmationModal = inject(OpenConfirmationModalService);

  protected readonly learnMoreComponents = new Map(
    Object.entries({
      DefaultLeadCapture: EnableLeadCaptureDialogComponent,
    }),
  );

  readonly assistant = toSignal(this.aiAssistantServiceV2.aiAssistant$);

  readonly AiCapabilityCustomForms = AiCapabilityCustomForms;

  private readonly capabilitiesUpdateTrigger = signal(0);

  protected readonly visibleCapabilityIndices = computed(() => {
    // Include a trigger to force recalculation when capabilities are modified
    this.capabilitiesUpdateTrigger();

    const formArray = this.capabilities();
    const visibleIndices: number[] = [];

    for (let i = 0; i < formArray.controls.length; i++) {
      if (!formArray.at(i).isHidden) {
        visibleIndices.push(i);
      }
    }

    return visibleIndices;
  });

  handleCapabilityClicked(capability: AiCapabilityForm, event: Event) {
    event.stopPropagation();
    this.capabilityFormDisplay.emit(capability);
  }

  handleCapabilityRemoveClicked(event: Event, i: number) {
    event.stopPropagation();

    const capabilityForm = this.capabilities().at(i);
    const situationConfigForm = this.situationConfig();

    const isUsedInSituations =
      situationConfigForm && capabilityForm
        ? situationConfigForm.getSituationsReferencingCapability(capabilityForm.toGoalKey()).length > 0
        : false;

    const message = isUsedInSituations
      ? 'AI_ASSISTANT.SETTINGS.REMOVE_CAPABILITY_WITH_SITUATIONS_CONFIRMATION'
      : 'AI_ASSISTANT.SETTINGS.REMOVE_CAPABILITY_CONFIRMATION';

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: 'AI_ASSISTANT.SETTINGS.REMOVE_CAPABILITY_TITLE',
        message,
        confirmButtonText: 'AI_ASSISTANT.SETTINGS.REMOVE',
      })
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          if (situationConfigForm && capabilityForm) {
            situationConfigForm.removeCapabilityFromMappings(capabilityForm.toGoalKey());
          }

          this.capabilities().removeAt(i);
          this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
        }
      });
  }

  handleLearnMoreClicked(capabilityID: string, event: Event) {
    event.stopPropagation();
    if (this.learnMoreComponents.has(capabilityID)) {
      const learnMoreComponent = this.learnMoreComponents.get(capabilityID);
      if (learnMoreComponent) {
        this.dialog.open(learnMoreComponent, {
          width: '660px',
          data: { source: 'capabilities-list' },
        });
      }
    }
  }

  capabilityClickable(capability: AiCapabilityForm): boolean {
    const goalId = capability.controls.goalId.getRawValue() || '';
    const customForm = this.AiCapabilityCustomForms[goalId];

    return !this.hasCustomForm(capability) && !(goalId in this.AiCapabilityCustomForms && customForm === null);
  }

  hasCustomForm(capability: AiCapabilityForm): boolean {
    return !!this.AiCapabilityCustomForms[capability.controls.goalId.getRawValue() || ''];
  }

  async handleCapabilityAddClicked() {
    const dialogRef = this.dialog.open(AiCapabilitySelectComponent, {
      viewContainerRef: this.viewContainerRef,
      width: '1100px',
      height: '700px',
      data: {
        assistantType: this.assistant()?.assistant?.type,
        excludedGoalIds: [
          ...this.capabilities()
            .controls.map((capabilityForm) => capabilityForm.controls.goalId.value)
            .filter((id) => id != null),
          ...this.capabilities()
            .controls.map((capabilityForm) => capabilityForm.controls.overrideOf.getRawValue()?.id)
            .filter((id) => !!id),
        ],
      },
    });

    dialogRef.afterClosed().subscribe(async (result: { goals?: GoalInterface[]; newGoal: boolean } | '') => {
      if (result === '' || !result) {
        return;
      }

      const { goals: chosenGoals, newGoal } = result;

      if (chosenGoals && chosenGoals.length > 0) {
        const results = await Promise.allSettled(
          chosenGoals.map(async (goal) => {
            let promptModuleContents: Record<string, string> = {};
            const promptModuleKeys =
              goal?.promptModules?.map((pm) => new PromptModuleKey({ id: pm.id, namespace: pm.namespace })) || [];

            if (promptModuleKeys.length > 0) {
              promptModuleContents = await this.assistantService.getMultiPromptModuleVersions(promptModuleKeys);
            }

            const newCapabilityForm = await AiCapabilityForm.createWithFunctionDetails(
              this.promptModuleApiService,
              { goal, configuration: [] },
              promptModuleContents,
              this.toolFormRegistry,
              this.functionApiService,
            );

            return { goal, form: newCapabilityForm };
          }),
        );

        const successes = results.filter(
          (result): result is PromiseFulfilledResult<{ goal: any; form: AiCapabilityForm }> =>
            result.status === 'fulfilled',
        );
        const failures = results.filter((result) => result.status === 'rejected');

        // Add successful capabilities to the form
        successes.forEach(({ value: { form } }) => {
          this.capabilities().push(form);
        });

        // Show appropriate feedback messages
        if (successes.length > 0) {
          this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
        }

        if (failures.length > 0) {
          // Show error message for any failures
          this.snackbar.openErrorSnack('AI_ASSISTANT.GOALS.ADD_GOAL.ERROR_LOADING_CAPABILITY');
        }
      } else if (newGoal) {
        const newCapabilityForm = new AiCapabilityForm(
          this.promptModuleApiService,
          undefined,
          undefined,
          this.toolFormRegistry,
        );
        // Don't add to capabilities array yet - wait for "Done" to be clicked
        // Auto-display the new capability form
        this.capabilityFormDisplay.emit(newCapabilityForm);
        this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
      }
    });
  }

  moveCapability(fromIndex: number, toIndex: number): void {
    const formArray = this.capabilities();

    // Don't move if indices are invalid
    if (toIndex < 0 || toIndex >= formArray.controls.length) {
      return;
    }

    const control = formArray.at(fromIndex);
    formArray.removeAt(fromIndex);
    formArray.insert(toIndex, control);
    this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
  }

  private getPreviousVisibleIndex(currentIndex: number): number | null {
    const visible = this.visibleCapabilityIndices();
    const position = visible.indexOf(currentIndex);
    return position > 0 ? visible[position - 1] : null;
  }

  private getNextVisibleIndex(currentIndex: number): number | null {
    const visible = this.visibleCapabilityIndices();
    const position = visible.indexOf(currentIndex);
    return position >= 0 && position < visible.length - 1 ? visible[position + 1] : null;
  }

  /**
   * Moves a capability up to the position above the first visible capability above it
   */
  moveCapabilityUp(index: number): void {
    const previousVisibleIndex = this.getPreviousVisibleIndex(index);

    if (previousVisibleIndex !== null) {
      this.moveCapability(index, previousVisibleIndex);
    }
  }

  /**
   * Moves a capability down to the position below the first visible capability below it
   */
  moveCapabilityDown(index: number): void {
    const nextVisibleIndex = this.getNextVisibleIndex(index);

    if (nextVisibleIndex !== null) {
      this.moveCapability(index, nextVisibleIndex);
    }
  }

  isMoveUpDisabled(index: number): boolean {
    const visible = this.visibleCapabilityIndices();
    return visible.length > 0 && visible[0] === index;
  }

  isMoveDownDisabled(index: number): boolean {
    const visible = this.visibleCapabilityIndices();
    return visible.length > 0 && visible[visible.length - 1] === index;
  }

  handleRollbackClicked(capability: AiCapabilityForm, event: Event) {
    event.stopPropagation();
    this.rollback.emit(capability);
  }
}
